import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Center, Stack, Text, Title, Alert } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * When any value in this array changes after an error was caught, the
   * boundary resets and re-renders its children. Pass the current route so a
   * crash on one page does not leave the whole app stuck on the fallback while
   * the user navigates elsewhere.
   */
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-level error boundary. Catches render/runtime errors thrown by descendants
 * (which would otherwise unmount the whole tree and leave a blank white screen),
 * reports them to Sentry, and shows a recoverable fallback instead. Resets
 * automatically when `resetKeys` change (e.g. on navigation).
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (!this.state.hasError) return;
    const prevKeys = prevProps.resetKeys ?? [];
    const nextKeys = this.props.resetKeys ?? [];
    const changed =
      prevKeys.length !== nextKeys.length || nextKeys.some((key, i) => key !== prevKeys[i]);
    if (changed) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <Center h="100vh" p="md">
        <Stack align="center" gap="md" maw={520}>
          <IconAlertTriangle size={64} color="var(--mantine-color-red-6)" />
          <Title order={2} ta="center">
            Algo salió mal
          </Title>
          <Text c="dimmed" ta="center">
            Ocurrió un error inesperado al mostrar esta pantalla. El equipo técnico ya fue
            notificado. Puedes volver al inicio o recargar la página.
          </Text>
          {this.state.error?.message && (
            <Alert
              variant="light"
              color="red"
              title="Detalle del error"
              icon={<IconAlertTriangle />}
              w="100%"
            >
              <Text size="sm" style={{ wordBreak: 'break-word' }}>
                {this.state.error.message}
              </Text>
            </Alert>
          )}
          <Button.Group>
            <Button variant="light" onClick={this.handleReload}>
              Recargar página
            </Button>
            <Button component="a" href="/" variant="filled">
              Ir al inicio
            </Button>
          </Button.Group>
        </Stack>
      </Center>
    );
  }
}

export default ErrorBoundary;
